// RelationshipMeter — the "AI Relationship Score" from the Client
// Success Manager spec. Same reusable pattern as the Compliance
// Readiness Meter — pure scoring + widgets + async input loader.
//
// Different factors than Compliance:
//
//   30%  Last conversation recency (last_meaningful_conversation_at,
//        else last inbound message)
//   15%  Responsiveness (inbound replies within 7d of outbound)
//   15%  Annual review completion (aep_review_status = completed)
//   15%  Open service issues (unresolved satisfaction_events)
//   10%  Satisfaction signal (NPS + compliment count)
//   10%  Referrals given (any in last year)
//    5%  Coverage review recency (last_ancillary_review_at)
//
// Buckets — deliberately three, not four:
//   strong          ≥ 80   🟢 Relationship Strong
//   needs_attention 50-79  🟡 Needs Attention
//   at_risk         < 50   🔴 At Risk

import React from 'react';
import { supabase } from '../lib/supabase';

const DAY_MS = 86_400_000;

async function safe(fn, fb) { try { const r = await fn(); return r ?? fb; } catch { return fb; } }

const LEVELS = [
  { key: 'strong',          min: 80, label: 'Relationship Strong', dot: 'bg-emerald-500', text: 'text-emerald-800', bg: 'bg-emerald-50', border: 'border-emerald-200', emoji: '🟢' },
  { key: 'needs_attention', min: 50, label: 'Needs Attention',     dot: 'bg-amber-400',   text: 'text-amber-800',   bg: 'bg-amber-50',   border: 'border-amber-200',   emoji: '🟡' },
  { key: 'at_risk',         min: 0,  label: 'At Risk',             dot: 'bg-rose-500',    text: 'text-rose-700',    bg: 'bg-rose-50',    border: 'border-rose-200',    emoji: '🔴' },
];
export function relationshipBucketFor(pct) { return LEVELS.find(l => pct >= l.min) || LEVELS[LEVELS.length - 1]; }

/* Pure scoring — no I/O. Inputs come from loadRelationshipInputs. */
export function computeRelationshipScore({ profile, lastInbound, outboundCount, inboundReplyCount, referralsCount }) {
  const now = Date.now();
  const factors = [];

  // 30% — Last conversation recency
  const lastAt = profile?.last_meaningful_conversation_at
    ? new Date(profile.last_meaningful_conversation_at).getTime()
    : lastInbound ? new Date(lastInbound).getTime() : null;
  const daysSince = lastAt ? (now - lastAt) / DAY_MS : null;
  const recencyScore = daysSince == null ? 0
                     : daysSince < 30  ? 100
                     : daysSince < 90  ? 70
                     : daysSince < 180 ? 40
                     :                   10;
  factors.push({ key: 'recency', weight: 0.30, score: recencyScore,
    label: 'Last meaningful conversation',
    reason: daysSince == null ? 'no conversation on file' : `${Math.round(daysSince)}d ago` });

  // 15% — Responsiveness (reply rate on outbound)
  const responsivenessScore = outboundCount > 0
    ? Math.min(100, Math.round((inboundReplyCount / outboundCount) * 100 * 1.4))
    : 50;
  factors.push({ key: 'responsiveness', weight: 0.15, score: responsivenessScore,
    label: 'Responsiveness',
    reason: outboundCount > 0 ? `${inboundReplyCount}/${outboundCount} replies` : 'no outbound to score' });

  // 15% — Annual review complete
  const reviewScore = profile?.aep_review_status === 'completed' ? 100 : 30;
  factors.push({ key: 'annual_review', weight: 0.15, score: reviewScore,
    label: 'Annual review',
    reason: profile?.aep_review_status === 'completed' ? 'completed this cycle' : 'not completed' });

  // 15% — Open service issues (each open one drops 30 points, floored at 0)
  const openIssues = (profile?.satisfaction_events || []).filter(e => e.kind === 'service_issue' && !e.resolved_at).length;
  const issuesScore = Math.max(0, 100 - openIssues * 30);
  factors.push({ key: 'service_issues', weight: 0.15, score: issuesScore,
    label: 'Open service issues',
    reason: openIssues === 0 ? 'none open' : `${openIssues} unresolved` });

  // 10% — Satisfaction signal (NPS + compliments)
  const events = profile?.satisfaction_events || [];
  const npsScore = events.filter(e => e.kind === 'nps' && e.score != null)
    .reduce((s, e, _, arr) => s + Number(e.score) / arr.length, 0);
  const complimentCount = events.filter(e => e.kind === 'compliment').length;
  const complaintCount  = events.filter(e => e.kind === 'complaint').length;
  const satisfactionScore = Math.max(0, Math.min(100,
    (npsScore ? npsScore * 10 : 50) + complimentCount * 10 - complaintCount * 20
  ));
  factors.push({ key: 'satisfaction', weight: 0.10, score: satisfactionScore,
    label: 'Satisfaction signal',
    reason: npsScore ? `NPS ${Math.round(npsScore)}, ${complimentCount} compliment${complimentCount === 1 ? '' : 's'}, ${complaintCount} complaint${complaintCount === 1 ? '' : 's'}`
                     : `${complimentCount} compliment${complimentCount === 1 ? '' : 's'}, ${complaintCount} complaint${complaintCount === 1 ? '' : 's'}` });

  // 10% — Referrals given
  const referralScore = referralsCount > 0 ? 100 : 40;
  factors.push({ key: 'referrals', weight: 0.10, score: referralScore,
    label: 'Referrals given',
    reason: referralsCount > 0 ? `${referralsCount} referral${referralsCount === 1 ? '' : 's'} given` : 'none' });

  // 5% — Coverage review recency
  const ancillaryScore = profile?.last_ancillary_review_at
    ? (((now - new Date(profile.last_ancillary_review_at).getTime()) / DAY_MS) <= 365 ? 100 : 30)
    : 30;
  factors.push({ key: 'ancillary_review', weight: 0.05, score: ancillaryScore,
    label: 'Ancillary coverage review',
    reason: profile?.last_ancillary_review_at ? 'within 12mo' : 'never reviewed' });

  const pct = Math.round(factors.reduce((s, f) => s + f.score * f.weight, 0));
  const bucket = relationshipBucketFor(pct);
  const weakestFactor = [...factors].sort((a, b) => (a.score * a.weight) - (b.score * b.weight))[0];

  return { pct, bucket, factors, weakestFactor };
}

/* Fetch inputs for a single client. Bounded — all one round-trip. */
export async function loadRelationshipInputs(clientId, ownerId) {
  const [profile, messages] = await Promise.all([
    safe(async () => {
      const { data } = await supabase.from('client_medical_profile')
        .select('last_meaningful_conversation_at, aep_review_status, satisfaction_events, referrals_given, last_ancillary_review_at')
        .eq('owner_id', ownerId).eq('client_id', clientId).maybeSingle();
      return data;
    }, null),
    safe(async () => {
      const { data } = await supabase.from('messages')
        .select('direction, sent_at').eq('lead_id', String(clientId))
        .order('sent_at', { ascending: false }).limit(200);
      return data || [];
    }, []),
  ]);

  const outbound = messages.filter(m => m.direction === 'out').sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
  const inbound  = messages.filter(m => m.direction === 'in').sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));

  // Count outbound messages that received an inbound reply within 7 days
  let inboundReplyCount = 0;
  for (const out of outbound) {
    const outAt = new Date(out.sent_at).getTime();
    if (inbound.some(inMsg => {
      const inAt = new Date(inMsg.sent_at).getTime();
      return inAt > outAt && (inAt - outAt) <= 7 * DAY_MS;
    })) inboundReplyCount++;
  }

  const lastInbound = inbound.length > 0 ? inbound[inbound.length - 1].sent_at : null;
  const referralsCount = Array.isArray(profile?.referrals_given) ? profile.referrals_given.length : 0;

  return { profile, lastInbound, outboundCount: outbound.length, inboundReplyCount, referralsCount };
}

/* ─────────────────────────────────────────────────────────────
   Widgets
   ───────────────────────────────────────────────────────────── */

export function RelationshipBadge({ pct, bucket }) {
  const b = bucket || relationshipBucketFor(pct);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border ${b.border} ${b.bg} px-2.5 py-0.5`}>
      <span className={`w-2 h-2 rounded-full ${b.dot}`}/>
      <span className={`text-[10.5px] font-bold uppercase tracking-wider ${b.text}`}>{b.label}</span>
      {pct != null && <span className={`text-[10.5px] font-black tabular-nums ${b.text}`}>{pct}%</span>}
    </span>
  );
}

/* Why the score is what it is — sorted worst-contributing first. */
export function RelationshipDetailPanel({ pct, bucket, factors, weakestFactor }) {
  const b = bucket || relationshipBucketFor(pct);
  const sorted = [...(factors || [])].sort((a, b) => (a.score * a.weight) - (b.score * b.weight));
  return (
    <div className={`border rounded-2xl p-5 ${b.border} ${b.bg}`}>
      <div className="flex items-baseline justify-between">
        <div>
          <div className={`text-[10.5px] font-bold uppercase tracking-[0.2em] ${b.text} opacity-80`}>Relationship score</div>
          <div className={`text-[36px] font-black tabular-nums leading-none mt-2 ${b.text}`}>{pct}%</div>
          <div className={`text-[12px] mt-1 ${b.text}`}>{b.emoji} {b.label}</div>
        </div>
        {weakestFactor && (
          <div className="text-right max-w-[220px]">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Biggest drag</div>
            <div className="text-[12px] font-bold text-slate-800">{weakestFactor.label}</div>
            <div className="text-[11px] text-slate-600">{weakestFactor.reason}</div>
          </div>
        )}
      </div>
      <div className="mt-4 space-y-1.5">
        {sorted.map(f => (
          <div key={f.key} className="flex items-center gap-3 text-[12px]">
            <div className="w-40 shrink-0 text-slate-700">{f.label}</div>
            <div className="flex-1 h-1.5 bg-white/60 rounded-full overflow-hidden max-w-[220px]">
              <div className={`h-full ${f.score >= 80 ? 'bg-emerald-500' : f.score >= 50 ? 'bg-amber-400' : 'bg-rose-500'}`} style={{ width: `${f.score}%` }}/>
            </div>
            <div className="tabular-nums text-slate-800 font-mono w-9 text-right">{Math.round(f.score * f.weight)}</div>
            <div className="text-slate-500 text-[11px] italic">{f.reason}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
